import { motion } from "framer-motion";

import SmartLink from "@/components/SmartLink";
import { Button } from "@/components/ui/button";
import { iconRegistry } from "@/content/iconRegistry";
import { trackCheckoutClick } from "@/lib/conversionTracking";

export default function CtaBannerSection({ content, dataTestId, checkoutProduct = "workshop" }) {
  const Icon = content.icon ? iconRegistry[content.icon] : null;
  const isDark = content.tone === "dark";

  return (
    <section className={`py-16 md:py-24 ${isDark ? "" : "bg-muted/50"}`.trim()} data-testid={dataTestId}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className={`rounded-3xl p-8 md:p-12 lg:p-16 text-center relative overflow-hidden ${
            isDark ? "bg-gradient-to-br from-secondary via-secondary to-secondary/78 border border-secondary/45 shadow-medium" : "bg-card border border-border shadow-soft"
          }`}
        >
          <div className="relative z-10">
            {Icon ? <Icon className={`w-12 h-12 mx-auto mb-4 ${isDark ? "text-highlight/90" : "text-highlight"}`} /> : null}
            <h2 className={`font-sans text-3xl md:text-4xl font-bold mb-4 ${isDark ? "text-white" : "text-foreground"}`}>
              {content.title}
            </h2>
            <p
              className={`font-body text-lg max-w-2xl mx-auto mb-8 ${
                isDark ? "text-white/82" : "text-muted-foreground"
              }`}
            >
              {content.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className={`rounded-full font-medium px-8 btn-hover ${
                  isDark ? "bg-primary text-white hover:bg-primary/90" : "bg-primary hover:bg-primary/90 text-white"
                }`}
              >
                <SmartLink
                  href={content.primaryCta.href}
                  external={content.primaryCta.external}
                  onClick={() =>
                    trackCheckoutClick({
                      product: checkoutProduct,
                      label: content.primaryCta.label,
                      href: content.primaryCta.href,
                      location: "cta_banner",
                    })
                  }
                >
                  {content.primaryCta.label}
                </SmartLink>
              </Button>
              {content.secondaryCta ? (
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className={`rounded-full px-8 font-medium ${isDark ? "border-transparent bg-white/10 text-white hover:bg-white/14 btn-hover" : "border-transparent bg-secondary text-white hover:bg-secondary/85 btn-hover"}`}
                >
                  <SmartLink href={content.secondaryCta.href} external={content.secondaryCta.external}>
                    {content.secondaryCta.label}
                  </SmartLink>
                </Button>
              ) : null}
            </div>
          </div>
          {isDark ? (
            <>
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary/12 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/12 rounded-full blur-3xl" />
            </>
          ) : null}
        </motion.div>
      </div>
    </section>
  );
}
